<template>
  <div>
    <div class="d-flex align-center mb-3">
      <div class="text-body-2 grey--text text--darken-1">
        Filters people set before running the report. In SQL use <code>:name</code>; a date range called <code>range</code> becomes
        <code>:range_from</code> / <code>:range_to</code>; a multi-select <code>ids</code> adds <code>:ids_any</code> (true when nothing chosen).
      </div>
      <v-spacer />
      <v-btn color="primary" small @click="add"><v-icon left small>mdi-plus</v-icon>Add filter</v-btn>
    </div>

    <draggable v-model="tpl.params" handle=".rb-handle">
      <v-card v-for="(p, i) in tpl.params" :key="i" outlined class="mb-3">
        <v-card-text>
          <v-row dense align="center">
            <v-col cols="auto"><v-icon class="rb-handle" style="cursor: grab">mdi-drag</v-icon></v-col>
            <v-col cols="12" sm="3"><v-text-field v-model="p.name" outlined dense hide-details label="Name (used in SQL)" /></v-col>
            <v-col cols="12" sm="3"><v-text-field v-model="p.label" outlined dense hide-details label="Label shown" /></v-col>
            <v-col cols="12" sm="2"><v-select v-model="p.type" :items="types" outlined dense hide-details label="Type" @change="typeChanged(p)" /></v-col>
            <v-col cols="auto"><v-checkbox v-model="p.required" dense hide-details label="Required" class="mt-0" /></v-col>
            <v-col v-if="p.type === 'select' || p.type === 'number'" cols="auto"><v-checkbox v-model="p.multiple" dense hide-details label="Multiple" class="mt-0" /></v-col>
            <v-spacer />
            <v-col cols="auto"><v-btn icon small @click="tpl.params.splice(i, 1)"><v-icon small>mdi-delete-outline</v-icon></v-btn></v-col>
          </v-row>

          <!-- default -->
          <v-row dense class="mt-1">
            <template v-if="p.type === 'daterange'">
              <v-col cols="6" sm="3"><v-combobox v-model="rangeDefault(p).from" :items="dateTokens" outlined dense hide-details label="Default from" /></v-col>
              <v-col cols="6" sm="3"><v-combobox v-model="rangeDefault(p).to" :items="dateTokens" outlined dense hide-details label="Default to" /></v-col>
            </template>
            <v-col v-else-if="p.type === 'date'" cols="12" sm="4"><v-combobox v-model="p.default" :items="dateTokens" outlined dense hide-details label="Default" /></v-col>
            <v-col v-else-if="p.type === 'boolean'" cols="12" sm="4"><v-select v-model="p.default" :items="[{ text: 'No', value: false }, { text: 'Yes', value: true }]" outlined dense hide-details clearable label="Default" /></v-col>
            <v-col v-else-if="p.type !== 'select'" cols="12" sm="4"><v-text-field v-model="p.default" outlined dense hide-details label="Default" /></v-col>
          </v-row>

          <!-- select options -->
          <div v-if="p.type === 'select'" class="mt-3">
            <v-btn-toggle :value="p.optionsQuery !== undefined ? 'query' : 'static'" mandatory dense class="mb-2" @change="optionMode(p, $event)">
              <v-btn small value="static">Fixed options</v-btn>
              <v-btn small value="query">Options from the database</v-btn>
            </v-btn-toggle>
            <div v-if="p.optionsQuery !== undefined">
              <div class="text-caption grey--text mb-1">SQL returning <code>value</code> and <code>label</code> columns.</div>
              <code-box v-model="p.optionsQuery" mode="sql" :rows="3" />
            </div>
            <div v-else>
              <v-row v-for="(o, k) in p.options || []" :key="k" dense>
                <v-col cols="5"><v-text-field v-model="o.value" outlined dense hide-details label="Value" /></v-col>
                <v-col cols="5"><v-text-field v-model="o.label" outlined dense hide-details label="Label" /></v-col>
                <v-col cols="2"><v-btn icon small @click="p.options.splice(k, 1)"><v-icon small>mdi-close</v-icon></v-btn></v-col>
              </v-row>
              <v-btn small text @click="addOption(p)"><v-icon left small>mdi-plus</v-icon>Add option</v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </draggable>
    <v-alert v-if="!tpl.params.length" type="info" text dense>No filters - the report always runs the same way.</v-alert>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import CodeBox from './CodeBox.vue'
import { state, uniqueName } from './editorState'

export default {
  name: 'RbFiltersTab',
  components: { draggable, CodeBox },
  data () {
    return {
      types: [
        { text: 'Date range', value: 'daterange' }, { text: 'Date', value: 'date' }, { text: 'Choose from a list', value: 'select' },
        { text: 'Number', value: 'number' }, { text: 'Text', value: 'string' }, { text: 'Yes / No', value: 'boolean' }
      ],
      dateTokens: ['today', 'yesterday', 'startOfMonth', 'endOfMonth', 'startOfLastMonth', 'endOfLastMonth', 'startOfYear', '-7d', '-30d', '-90d']
    }
  },
  computed: { tpl () { return state.tpl } },
  methods: {
    add () {
      const name = uniqueName('filter', this.tpl.params.map((p) => p.name))
      this.tpl.params.push({ name, type: 'string', label: 'New filter' })
    },
    rangeDefault (p) {
      if (!p.default || typeof p.default !== 'object') this.$set(p, 'default', { from: 'startOfMonth', to: 'today' })
      return p.default
    },
    typeChanged (p) {
      this.$set(p, 'default', undefined)
      if (p.type !== 'select') { this.$delete(p, 'options'); this.$delete(p, 'optionsQuery') }
      if (p.type === 'select' && !p.options && p.optionsQuery === undefined) this.$set(p, 'options', [])
    },
    optionMode (p, mode) {
      if (mode === 'query') { this.$delete(p, 'options'); this.$set(p, 'optionsQuery', 'SELECT id AS value, name AS label FROM "Locations" ORDER BY name') }
      else { this.$delete(p, 'optionsQuery'); this.$set(p, 'options', p.options || []) }
    },
    addOption (p) {
      if (!p.options) this.$set(p, 'options', [])
      p.options.push({ value: '', label: '' })
    }
  }
}
</script>
